<template>
    <div class="relative">
        <section v-if="order">

            <div
                class="gap-6 grid grid-cols-3"
                :class="{
                    'px-8 pt-6 pb-10': !fromDialog,
                }"
            >

                <div class="flex flex-col h-full">
                    <div class="bg-gray-tier-5 rounded-lg grow flex flex-col" :class="fromDialog ? 'p-6 text-sm' : 'p-7'">
                        <div class="mb-4">
                            <span class="type-secondary text-base">{{ $t("Order Total") }}</span>
                            <h5 class="type-primary text-2xl font-medium">{{ showMeTheMoney(order.total) }}</h5>
                        </div>
                        <div v-if="isEmployee && order.gross_profit" class="flex items-center justify-between border-b border-t border-keyline-tertiary py-1.5">
                            <span class="type-secondary">{{ $t("GP") }}</span>
                            <span class="type-primary">{{ order.gross_profit }}%</span>
                        </div>
                        <div class="flex items-center justify-between border-b border-keyline-tertiary py-1.5">
                            <span class="type-secondary">{{ $t("Freight") }}</span>
                            <span class="type-primary tabular-nums">{{ showMeTheMoney(order.freight) }}</span>
                        </div>
                        <div class="flex items-center justify-between border-b border-keyline-tertiary py-1.5">
                            <span class="type-secondary">{{ $t("Tax") }}</span>
                            <span class="type-primary tabular-nums">{{ showMeTheMoney(order.tax) }}</span>
                        </div>
                        <div class="flex items-center justify-between border-b border-keyline-tertiary py-1.5">
                            <span class="type-secondary">{{ $t("Sub Total") }}</span>
                            <span class="type-primary tabular-nums">{{ showMeTheMoney(order.subtotal) }}</span>
                        </div>
                        <div class="flex items-center justify-between border-b border-keyline-tertiary py-1.5">
                            <span class="type-secondary">{{ $t("Total") }}</span>
                            <span class="type-primary font-bold tabular-nums">{{ showMeTheMoney(order.total) }}</span>
                        </div>
                        <div class="flex items-center justify-between border-b border-keyline-tertiary py-1.5">
                            <span class="type-secondary">{{ $t("Amount Due") }}</span>
                            <span
                                class="type-primary font-bold tabular-nums"
                                :class="{
                                    'text-orange': order.balance_due > 0,
                                }"
                            >
                                {{ showMeTheMoney(order.balance_due) }}
                            </span>
                        </div>
                        <div v-if="order.balance_due > 0" class="flex items-center justify-between py-1.5">
                            <span class="type-secondary">{{ $t("Amount Paid") }}</span>
                            <span>
                                <BaseSvg name="icon-check" class="size-5 text-green" v-if="order.balance_due <= 0" />
                                <span class="tabular-nums">{{ showMeTheMoney(order.amount_paid) }}</span>
                            </span>
                        </div>
                    </div>
                </div>

                <div class="bg-gray-tier-5 rounded-lg flex flex-col leading-relaxed" :class="{ 'text-sm': fromDialog }">
                    <div class="pb-2" :class="fromDialog ? 'p-6' : 'p-7'">
                        <h5 class="type-primary block text-base">
                            <b>{{ order.customer?.name }}</b>
                        </h5>
                        <ul>
                            <li>
                                <span class="type-secondary mr-2">{{$t("Customer ID") }}</span>
                                <span class="type-primary font-medium">{{order.customer?.id}}</span>
                            </li>
                            <li v-if="isEmployee && !fromSales">
                                <span class="type-secondary mr-2">{{$t("Customer Group") }}</span>
                                <span class="type-primary font-medium">{{order.customer?.group}}</span>
                            </li>
                            <li>
                                <span class="type-secondary mr-2">{{$t("Free Freight") }}</span>
                                <span class="type-primary font-medium">{{showMeTheMoney(order.customer?.free_shipping_at)}}</span>
                            </li>
                            <li v-if="order.customer?.terms_code">
                                <span class="type-secondary mr-2">{{$t("Terms") }}</span>
                                <span class="type-primary font-medium">{{order.customer?.terms_code }}</span>
                            </li>
                        </ul>
                    </div>
                    <div class="grow pl-2 pr-4" v-if="!hasRole('Restrict Account History')">
                        <apex-chart
                            v-if="salesData"
                            height="90%"
                            :options="chartOptions"
                            :series="series"
                        />
                        <div v-else class="flex items-center justify-center h-full text-gray-400">
                            <span class="text-sm">{{ $t('Sales data not available') }}</span>
                        </div>
                    </div>
                </div>

                <div class="bg-gray-tier-5 rounded-lg leading-relaxed" :class="fromDialog ? 'p-6 text-sm' : 'p-7'">
                    <div v-if="order.address">
                        <AddressCard
                            :header="$t('Shipping Address')"
                            :address="order.address"
                        />
                        <hr class="my-4 border-keyline-tertiary">
                        <AddressCard
                            v-if="order.customer?.address"
                            :header="$t('Billing Address')"
                            :address="order.customer.address"
                        />
                    </div>
                    <div v-else class="p-4 bg-gray-tier-5 mb-4">
                        <h5 class="type-primary font-medium">{{ $t("Ship to") }}:</h5>
                        <p class="type-secondary">{{ $t("VERIFY SHIPPING ADDRESS") }}</p>
                    </div>
                    <!-- <div class="h-32 w-full hidden at768:block">
                        <google-map id="shipping_map" :address="order.address"/>
                    </div> -->
                </div>
            </div>

            <RecordSection
                v-if="order.notes?.data?.length && isEmployee"
                :title="$t('Header Notes')"
                :record="order"
                class="mb-10"
                :class="{
                    'm-8 mt-0': !fromDialog,
                    'mt-10': fromDialog,
                }"
            >
                <TableNew
                    :columns="notesColumns"
                    :rows="order.notes?.data"
                    :row-component="TableRowTwo"
                    :fixed-first-column="true"
                    :edge-padding-num="fromDialog ? 0 : 6"
                    :header-component="null"
                />
            </RecordSection>

            <RecordSection
                v-if="invoiceRows?.length"
                :title="$t('Invoices')"
                :record="order"
                class="mb-10"
                :class="{
                    'm-8 mt-0': !fromDialog,
                    'mt-10': fromDialog,
                }"
            >
                <TableNew
                    :columns="invoiceColumns"
                    :rows="invoiceRows"
                    :row-component="TableRowTwo"
                    :fixed-first-column="true"
                    :edge-padding-num="fromDialog ? 0 : 6"
                />
            </RecordSection>

            <RecordSection
                v-if="order.status !== 'Processing'"
                :title="$t('Shipments')"
                :record="order"
                class="mb-10"
                :class="{
                    'm-8 mt-0': !fromDialog,
                    'mt-10': fromDialog,
                }"
            >
                <div v-if="Object.values(shipmentRows)?.length">
                    <TableNew
                        :rows="Object.values(shipmentRows)"
                        :columns="shipmentColumns"
                        :row-component="TableRowTwo"
                        :fixed-first-column="true"
                        :edge-padding-num="fromDialog ? 0 : 6"
                    />
                    <div
                        class="py-2 bg-gray-tier-4 hover:bg-gray-tier-5 transition-colors duration-100 border-t border-keyline-primary"
                        :class="{
                            'px-6': fromDialog,
                            'px-4': !fromDialog,
                            'bg-gray-tier-5': showShippingDelivery,
                        }"
                    >

                        <!-- Buttons  -->
                         <div class="flex items-center justify-end gap-2">
                            <AppButton
                                :link="order.tracking_link"
                                size="small"
                                icon="icon-open-in-new"
                                :reverse="true"
                                :text="$t('Track All')"
                            />

                            <AppButton
                                @click="loadShippingDelivery"
                                size="small"
                                icon="ExpandMore"
                                :reverse="true"
                                :icon-class="showShippingDelivery ? 'rotate-180 !type-primary' : ''"
                                :text="`${$t('View')} ${shipmentCount} ${pluralize(shipmentCount, 'shipment', 'shipments')}`"
                            />

                        </div>

                        <!-- shipments -->
                        <div
                            ref="container"
                            class="overflow-hidden transition-all duration-300 ease-in-out"
                            :class="{
                                'mt-4': showShippingDelivery,
                            }"
                            :style="{ height: contentHeight + 'px' }"
                        >
                            <!-- Show loading circle while API is fetching -->
                            <div v-if="showShippingDelivery && isLoading" class="p-8">
                                <LoadingCircle class="mx-auto" />
                            </div>

                            <!-- Show shipping delivery and pickup sections when toggled on -->
                            <div v-if="showShippingDelivery && !isLoading">
                                <PickupSection v-if="order.pickup_count > 0" :order="orderForShippingDelivery" :cards="cards" />
                                <ShippingDelivery v-if="shipments.length > 0" :shipments="shipments" :order="orderForShippingDelivery" />
                            </div>
                        </div>

                    </div>
                </div>
                <div v-else-if="isLoading" class="p-4">
                    <LoadingCircle class="mx-auto" />
                </div>
                <div v-else class="grid grid-cols-2 gap-6 p-4 pl-6">
                    <div class="type-tertiary">{{ $t("No tracking information") }}</div>
                    <div class="type-primary font-medium">{{ order.ship_via_code }}</div>
                </div>
            </RecordSection>

            <RecordSection
                :title="$t('Items')"
                :record="order"
                class="mb-8"
                :class="{
                    'm-8 mt-0': !fromDialog,
                    'mt-10': fromDialog,
                }"
            >
                <TableNew
                    :columns="lineColumns"
                    :rows="lineRows"
                    :row-component="TableRowTwo"
                    :edge-padding-num="fromDialog ? 0 : 6"
                />
            </RecordSection>

        </section>
    </div>
</template>

<script setup>
import {
    RecordSection,
    TableNew,
    TableRowTwo,
    LoadingCircle,
    AddressCard,
    BaseSvg,
    AppButton,
} from "@/Components";
import {
    ShippingDelivery,
    PickupSection,
} from "@/Themes/Marketing/Pages/Account/Orders/Detail";
import { useUser, useHelpers, useShipping, useApexCharts } from "@/Composables";
import _ from 'lodash';
import { ref, computed, nextTick, watch } from 'vue';

const { isEmployee, hasRole } = useUser();
const { showMeTheMoney } = useHelpers();
const { fetchTrackingInformation, shipments, isLoading } = useShipping();
const { pluralize } = useHelpers();
const { chart_options: chartOptions } = useApexCharts('chart_customer_sales');

const trackingNumbers = ref([]);
const showShippingDelivery = ref(false);
const container = ref(null);
const contentHeight = ref(0);

const props = defineProps({
    order: null,
    invoiceColumns: null,
    invoiceRows: null,
    shipmentRows: null,
    notesColumns: null,
    shipmentColumns: null,
    lineRows: null,
    lineColumns: null,
    cards: null,
    isLoading: null,
    salesData: null,
    fromDialog: {
        type: Boolean,
        default: false,
    },
    fromSales: {
        type: Boolean,
        default: false,
    },
});

// Computed property to provide the correct order structure for ShippingDelivery
const orderForShippingDelivery = computed(() => ({
    ...props.order,
    shipments: {
        data: Array.isArray(props.order?.shipments)
            ? props.order.shipments
            : props.order?.shipments?.data || []
    }
}));

// Get the count of shipments for the button text
const shipmentCount = computed(() => {
    const shipmentsData = Array.isArray(props.order?.shipments)
        ? props.order.shipments
        : props.order?.shipments?.data || [];
    return shipmentsData.length;
});

// Customer sales series for the apex chart
const series = computed(() => [
    {
        name: 'Sales',
        data: props.salesData?.sales_graph ? [...props.salesData.sales_graph].reverse() : [],
    },
]);

const loadShippingDelivery = async () => {
    // Toggle the visibility instead of just showing
    showShippingDelivery.value = !showShippingDelivery.value;

    if (showShippingDelivery.value) {
        // Expanding - first check if we need to load data
        if (shipments.value.length === 0) {
            // Set height for loading state
            contentHeight.value = 120; // Height for loading circle with padding

            // Handle both cases: shipments directly as array or wrapped in data property
            const shipmentsData = Array.isArray(props.order?.shipments)
                ? props.order.shipments
                : props.order?.shipments?.data || [];

            // Set shipments data for tracking API
            shipments.value = shipmentsData;

            // Extract tracking numbers for API enrichment
            trackingNumbers.value = _.flatMap(shipmentsData, 'tracking_number').filter(Boolean);

            // Call API to get enriched tracking information only if we have tracking numbers
            if (trackingNumbers.value.length > 0) {
                fetchTrackingInformation(trackingNumbers.value);
            }

            // Wait for content to load, then measure and set proper height
            await nextTick();
            setTimeout(async () => {
                if (container.value && !isLoading.value) {
                    const actualHeight = container.value.scrollHeight;
                    contentHeight.value = actualHeight;
                }
            }, 100);
        } else {
            // Already have data, just show it
            await nextTick();
            setTimeout(() => {
                if (container.value) {
                    // Measure the actual height of the container's content
                    const actualHeight = container.value.scrollHeight;
                    contentHeight.value = actualHeight;
                }
            }, 50); // Small delay to ensure content is rendered
        }
    } else {
        // Collapsing
        contentHeight.value = 0;
    }
};

// Watch for loading state changes to update height
watch(isLoading, async (newLoading) => {
    if (!newLoading && showShippingDelivery.value) {
        // Loading finished and content is visible, measure and set proper height
        await nextTick();
        setTimeout(() => {
            if (container.value) {
                const actualHeight = container.value.scrollHeight;
                contentHeight.value = actualHeight;
            }
        }, 100); // Slightly longer delay to ensure content is fully rendered
    }
});
</script>

<style>
/* tbody tr:last-child td {
    border-bottom: none;
} */
</style>
